import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Lang, SUPPORTED_LANGS, DEFAULT_LANG, RTL_LANGS, routeSlugs, findRouteKey, getLocalizedPath } from './routes';

export function useLanguage() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const currentLang = useMemo((): Lang => {
    const lang = i18n.language?.substring(0, 2) as Lang;
    return SUPPORTED_LANGS.includes(lang) ? lang : DEFAULT_LANG;
  }, [i18n.language]);

  const isRTL = RTL_LANGS.includes(currentLang);

  const switchLanguage = useCallback((newLang: Lang) => {
    // Check if we are in a dashboard/admin route (not prefixed with language)
    const segments = location.pathname.split('/').filter(Boolean);
    const isPublicRoute = SUPPORTED_LANGS.includes(segments[0] as Lang) || segments.length === 0 || findRouteKey(segments[0]);

    if (!isPublicRoute) {
      // For dashboard routes, just change the language and stay on current path
      i18n.changeLanguage(newLang);
      return;
    }

    // Find current route key from the current path for public routes
    // Remove language prefix if present
    let routeSegments = [...segments];
    if (SUPPORTED_LANGS.includes(segments[0] as Lang) && segments[0] !== 'es') {
      routeSegments = segments.slice(1);
    }

    const currentSlug = routeSegments[0] || '';
    const routeKey = findRouteKey(currentSlug) || 'home';
    const extraParams = routeSegments.slice(1).join('/');

    // Get the new path
    const newPath = getLocalizedPath(routeKey, newLang, extraParams || undefined);

    i18n.changeLanguage(newLang);
    navigate(newPath);
  }, [i18n, location.pathname, navigate]);

  /**
   * Get localized path for a route key
   */
  const localePath = useCallback((routeKey: string, params?: string): string => {
    return getLocalizedPath(routeKey, currentLang, params);
  }, [currentLang]);

  /**
   * Get localized href for anchor links on landing page
   */
  const localeAnchor = useCallback((anchor: string): string => {
    const prefix = currentLang === DEFAULT_LANG ? '' : `/${currentLang}`;
    return `${prefix || '/'}#${anchor}`;
  }, [currentLang]);

  return {
    currentLang,
    isRTL,
    switchLanguage,
    localePath,
    localeAnchor,
    supportedLangs: SUPPORTED_LANGS,
  };
}
